import { Component } from '@theme/component';

/** @extends {Component<{toggle: HTMLButtonElement}>} */
class CMMotion extends Component {
  #abort;
  #observer;
  #mobile = matchMedia('(max-width: 749px)');
  #reduced = matchMedia('(prefers-reduced-motion: reduce)');
  #visible = false;
  #userPaused = false;
  #manualPlay = false;
  #videos = [];

  connectedCallback() {
    super.connectedCallback();
    this.#setup();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#cleanup();
    this.#setup();
  }

  disconnectedCallback() {
    this.#cleanup();
    super.disconnectedCallback();
  }

  #setup() {
    this.#abort = new AbortController();
    const { signal } = this.#abort;
    this.#videos = [...this.querySelectorAll('video')];
    for (const video of this.#videos) {
      video.controls = false;
      video.muted = true;
      video.addEventListener('play', this.#updateButton, { signal });
      video.addEventListener('pause', this.#updateButton, { signal });
    }
    this.refs.toggle.hidden = false;
    this.#mobile.addEventListener('change', this.#sync, { signal });
    this.#reduced.addEventListener('change', this.#sync, { signal });
    document.addEventListener('visibilitychange', this.#sync, { signal });
    this.#observer = new IntersectionObserver(([entry]) => {
      this.#visible = entry.isIntersecting;
      this.#sync();
    }, { threshold: 0.15 });
    this.#observer.observe(this);
    this.#updateButton();
  }

  #cleanup() {
    this.#abort?.abort();
    this.#observer?.disconnect();
    for (const video of this.#videos) video.pause();
  }

  get activeVideo() {
    return this.#videos[this.#mobile.matches && this.#videos.length > 1 ? 1 : 0];
  }

  #sync = () => {
    const active = this.activeVideo;
    const allowMotion = this.#manualPlay || (!this.#reduced.matches && !navigator.connection?.saveData);
    for (const video of this.#videos) {
      if (video === active && this.#visible && !document.hidden && !this.#userPaused && allowMotion) {
        video.play().catch(this.#updateButton);
      } else {
        video.pause();
      }
    }
    this.#updateButton();
  };

  /** Toggle playback from the keyboard or pointer. */
  togglePlayback() {
    const video = this.activeVideo;
    if (!video) return;
    this.#userPaused = !video.paused;
    this.#manualPlay = !this.#userPaused;
    if (this.#userPaused) video.pause();
    else video.play().catch(this.#updateButton);
    this.#updateButton();
  }

  #updateButton = () => {
    const playing = Boolean(this.activeVideo && !this.activeVideo.paused);
    this.toggleAttribute('data-playing', playing);
    this.refs.toggle?.setAttribute('aria-label', playing ? this.dataset.pauseLabel : this.dataset.playLabel);
  };
}

if (!customElements.get('cm-motion-component')) customElements.define('cm-motion-component', CMMotion);
